/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import React, {useEffect, useState, useMemo} from 'react'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {useShopperObjectsBasketValidation} from '../../hooks/useScapiCustomApi'

// Chakra Components
import {
    Box,
    Stack,
    Grid,
    GridItem,
    Container,
    Progress,
    Text,
    Divider,
    HStack,
    VStack
} from '@chakra-ui/react'

// Project Components
import CartCta from './partials/cart-cta'
import CartOrderSummary from './partials/cart-order-summary'
import CartSkeleton from './partials/cart-skeleton'
import CartTitle from './partials/cart-title'
import CartDelivery from './partials/cart-delivery'
import CartStickyCta from './partials/cart-sticky-cta'

import ContentSlot from '../../components/content-slot'
import EmptyCart from './partials/empty-cart'
import RecommendedProducts from '../../components/recommended-products'
import RecommendedAccessories from '../../components/recommended-accessories'
import Callout from '../../components/content-asset'
import CartBonusOffer from './partials/cart-bonus-offer'
import GiftMessage from '../../components/gift-message'
import useBreakpoints from '../../hooks/use-breakpoints'

// Hooks
import {useCustomerGetShippingPromotionMessages} from '../../hooks/useScapiCustomApi'
import {useToast} from '../../hooks/use-toast'
import {useCurrentCustomer} from '../../hooks/use-current-customer'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import useEinstein from '../../hooks/use-einstein'
import Seo from '../../components/seo'
import {FreeShippingCircleIcon, FreeShippingFlagIcon, VerifiedIcon} from '../../components/icons'
import useMultiSite from '../../hooks/use-multi-site'
import {useHeader} from '../../hooks/use-header'
import {useNotifyMeModal, NotifyModal} from '../../hooks/use-notify-me-modal'
import useCartSkeletonLoading from './hooks/use-cart-skeleton-loading'
import {useCookieConsent} from '../../hooks/use-cookie-consent'
import {useCroSlot} from '../../components/cro'

// Constants
import {
    API_ERROR_MESSAGE,
    CART_ERRORS,
    CART_ITEM_QUANTITY_DEBOUNCE_WAIT,
    CRO_LOCATION
} from '../../constants'

// Utilities
import debounce from 'lodash/debounce'
import useTrackViewCart from './hooks/use-track-view-cart'
import {trackAddToCart, trackRemoveFromCart} from '../../tracking/analytics'
import {useInitialDataLayer} from '../../tracking/hooks/use-initial-data-layer'
import {getParameterValue} from '../../utils/url'
import {
    ProductQuickviewModal,
    useProductQuickviewModal
} from '../../components/product-quickview-modal'
import useEffectEvent from '../../hooks/use-effect-event'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import useProductItemsDetail from '../../hooks/useProductItemsDetail'
import {
    ShopperBasketsMutations,
    useShippingMethodsForShipment,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import useOcapiSite from '../../hooks/useOcapiSite'
import {useStokkeAddItemToBasketMutation} from '../../hooks/useStokkeHelpers'
import PayLater from '../../components/pay-later'
import CartLineItem from './partials/cart-line-item'
import {cacheUpdateMatrix as ShopperBasketCacheUpdateMatrix} from '@salesforce/commerce-sdk-react/hooks/ShopperBaskets/cache'
import ExpressCheckout from '../../components/express-checkout'
import useConvertPageTags from '../../convert-api/hooks/use-convert-page-tags'
import ModuleContainer from '../../leap/components/module-container'
import {HeaderVariant} from '../../leap/components/navigation/header/header-variant'

const Cart = () => {
    const {data: basket, isLoading} = useCurrentBasket()
    const {data: productItemsDetail, isLoading: isProductItemsDetailLoading} =
        useProductItemsDetail(basket)
    const einstein = useEinstein()
    const {data: customer} = useCurrentCustomer()
    const location = useLocation()
    const [message] = useState(location.state?.message)
    const [selectedItem, setSelectedItem] = useState(undefined)
    const [localQuantity, setLocalQuantity] = useState({})
    const [isCartItemLoading, setCartItemLoading] = useState(false)
    const [shippingMessages, setShippingMessages] = useState([])
    const {giftMessageEnabled} = useOcapiSite().preferences

    const {formatMessage, formatNumber} = useIntl()
    const toast = useToast()
    const {locale, site} = useMultiSite()
    const {header} = useHeader()
    const showError = (key) => {
        toast({
            title: formatMessage(key || API_ERROR_MESSAGE),
            status: 'error'
        })
    }
    const {data: shippingMethods} = useShippingMethodsForShipment(
        {
            parameters: {basketId: basket?.basketId, shipmentId: 'me'}
        },
        {
            enabled: !!basket?.basketId
        }
    )
    const isDesktop = useBreakpoints({lg: true})
    const addItemToBasketMutation = useStokkeAddItemToBasketMutation()
    const removeItemFromBasketMutation = useShopperBasketsMutation(
        ShopperBasketsMutations.RemoveItemFromBasket
    )
    const updateItemInBasketMutation = useShopperBasketsMutation(
        ShopperBasketsMutations.UpdateItemInBasket
    )
    const updateShippingMethodForShipmentMutation = useShopperBasketsMutation(
        ShopperBasketsMutations.UpdateShippingMethodForShipment
    )

    const {onOpen: onOpenNotifyMeModal, ...notifyMeModalProps} = useNotifyMeModal()

    const productQuickviewModalProps = useProductQuickviewModal()
    productQuickviewModalProps.onRecommendedProductClick = productQuickviewModalProps.onOpen

    const {skeletonShowing, pageLoaded} = useCartSkeletonLoading()
    const {marketing: marketingCookieAccepted} = useCookieConsent()

    const giftMessageData = useMemo(() => {
        return {
            giftMessage: basket?.c_giftMessageText || '',
            giftFrom: basket?.c_giftMessageSender || '',
            giftRecipientName: basket?.c_giftRecipientName || '',
            giftRecipientEmail: basket?.c_giftRecipientEmail || ''
        }
    }, [
        basket?.c_giftMessageSender,
        basket?.c_giftMessageText,
        basket?.c_giftRecipientName,
        basket?.c_giftRecipientEmail
    ])

    const updateBasketMutation = useShopperBasketsMutation(ShopperBasketsMutations.UpdateBasket)

    const updateGiftMessage = async (giftMessage) => {
        await updateBasketMutation.mutateAsync({
            body: giftMessage,
            parameters: {basketId: basket.basketId}
        })
    }

    useCroSlot(CRO_LOCATION.APPCART)

    useInitialDataLayer({pageType: 'Cart'})
    useConvertPageTags({pageType: 'cart'})

    useTrackViewCart()

    const onErrorMessage = useEffectEvent((message) => showError(message))
    useEffect(() => {
        if (message) {
            onErrorMessage(message)
        }
    }, [message, onErrorMessage])

    // check for basket validation
    const {data: validationResult} = useShopperObjectsBasketValidation({
        // always fetch on mount to make sure any cached results are invalidated
        refetchOnMount: 'always'
    })
    // show basket validation errors
    const onDecodedMessage = useEffectEvent((title) =>
        toast({
            title,
            status: 'error'
        })
    )
    const onDecodedMessageCleanup = useEffectEvent((toastId) => toast.close(toastId))
    useEffect(() => {
        // render error message only in case a basket exists
        // in case there is no basket - we error message is also missing
        // the page itself renders that there is nothing in the basket so we don't have to render any error
        // other approach is check for basket?.basketId
        if (validationResult && !validationResult.valid && validationResult.errorMessage) {
            const toastId = onDecodedMessage(validationResult.errorMessage)
            return () => {
                onDecodedMessageCleanup(toastId)
            }
        }
    }, [validationResult, onDecodedMessage, onDecodedMessageCleanup])
    // end show basket validation errors

    // invalidate basket validation cache on basket update
    // internally commerce-sdk-react uses the cache to know what cached data to invalidate
    // after a basket mutation. We add to all mutations to invalidate the custom api
    // for basket validation while the page is displayed. The cache is also invalidated
    // on page mount with the use of refetchOnMount: 'always'.
    useEffect(() => {
        // make a copy of the original update matrix
        const originalUpdateMatrix = {...ShopperBasketCacheUpdateMatrix}
        // replace the update matrix with our custom one
        for (const mutationKey in ShopperBasketCacheUpdateMatrix) {
            ShopperBasketCacheUpdateMatrix[mutationKey] = (...args) => {
                // call the original update matrix function
                const result = originalUpdateMatrix[mutationKey](...args)
                return {
                    ...result,
                    invalidate: [
                        ...(result.invalidate || []),
                        // append the basket validation cache to the list of caches to invalidate
                        {queryKey: useShopperObjectsBasketValidation.queryKey()}
                    ]
                }
            }
        }
        return () => {
            for (const mutationKey in ShopperBasketCacheUpdateMatrix) {
                ShopperBasketCacheUpdateMatrix[mutationKey] = originalUpdateMatrix[mutationKey]
            }
        }
    }, [])
    // end invalidate basket validation cache on basket update

    // set cache control header
    const {res} = useServerContext()
    if (res) {
        const cacheConfig = getConfig().app.cache
        if (cacheConfig?.cart) {
            res.set('Cache-Control', cacheConfig.cart)
        }
    }

    // show errors from checkout
    const cartErrorCode = useMemo(() => {
        if (location.state?.cartError) {
            return location.state.cartError
        }
        const pwaSerializedState = getParameterValue(location, 'pwaSerializedState')
        if (!pwaSerializedState) {
            return
        }
        try {
            return JSON.parse(decodeURIComponent(pwaSerializedState))?.cartError
        } catch {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('invalid PWA state structure')
            }
        }
    }, [location])
    const serializedCartError = useMemo(
        () => (CART_ERRORS[cartErrorCode] ? formatMessage(CART_ERRORS[cartErrorCode]) : undefined),
        [cartErrorCode, formatMessage]
    )
    const onSerializedCartError = useEffectEvent((title) =>
        toast({
            title,
            status: 'error'
        })
    )
    useEffect(() => {
        if (serializedCartError) {
            onSerializedCartError(serializedCartError)
        }
    }, [serializedCartError, onSerializedCartError])
    // end show errors from checkout

    // handle basket available
    const onBasketAvailable = useEffectEvent((basketId) => {
        // Set the default shipping method if none is already selected
        if (
            basketId &&
            basket.shipments.length > 0 &&
            !basket.shipments[0].shippingMethod &&
            shippingMethods?.defaultShippingMethodId
        ) {
            updateShippingMethodForShipmentMutation.mutate({
                body: {id: shippingMethods.defaultShippingMethodId},
                parameters: {basketId: basketId, shipmentId: 'me'}
            })
        }
    })
    useEffect(() => {
        onBasketAvailable(basket?.basketId)
    }, [basket?.basketId, onBasketAvailable])
    // end handle new basket

    const {mutateAsync: getPromoMessages} = useCustomerGetShippingPromotionMessages(
        {
            parameters: {
                siteId: site.id,
                locale: locale.id
            }
        },
        {
            onSuccess: (data) => {
                setShippingMessages(data.messages)
            }
        }
    )

    useEffect(() => {
        if ((basket?.orderTotal || basket?.productTotal) > 0) {
            getPromoMessages()
        }
    }, [basket?.orderTotal, basket?.productTotal, getPromoMessages])

    const isShippingPromoApplied = useMemo(() => {
        const pids = []
        shippingMessages.forEach((message) => {
            pids.push(message.id)
        })
        return (
            basket?.shippingItems?.[0]?.priceAdjustments?.length > 0 &&
            pids.indexOf(basket?.shippingItems?.[0]?.priceAdjustments?.[0]?.promotionId) === -1
        )
    }, [shippingMessages, basket?.shippingItems])

    const cartProducts = useMemo(() => {
        return basket?.productItems
            ?.map((item) => productItemsDetail[item.productId])
            .filter(Boolean)
    }, [basket?.productItems, productItemsDetail])

    const onChangeItemQuantity = useEffectEvent(async (quantity, product) => {
        setCartItemLoading(true)
        setSelectedItem(product)

        await updateItemInBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket?.basketId, itemId: product.itemId},
                body: {
                    productId: product.id,
                    quantity: parseInt(quantity)
                }
            },
            {
                onSettled: () => {
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                },
                onSuccess: () => {
                    setLocalQuantity((localQuantity) => ({
                        ...localQuantity,
                        [product.itemId]: undefined
                    }))
                },
                onError: () => {
                    // reset the quantity to the previous value
                    setLocalQuantity((localQuantity) => ({
                        ...localQuantity,
                        [product.itemId]: undefined
                    }))
                    showError()
                }
            }
        )
    })

    // need to memoize the debounced function, or it will create new instance every time
    // and never debounce, just delay
    const changeItemQuantity = useMemo(
        () => debounce(onChangeItemQuantity, CART_ITEM_QUANTITY_DEBOUNCE_WAIT),
        [onChangeItemQuantity]
    )

    if (isLoading) {
        skeletonShowing()
        return <CartSkeleton />
    }

    pageLoaded()

    if (!isLoading && !basket?.productItems?.length) {
        return (
            <>
                <EmptyCart
                    isRegistered={customer.isRegistered}
                    onRecommendedProductClick={productQuickviewModalProps.onRecommendedProductClick}
                />
                <ProductQuickviewModal {...productQuickviewModalProps} />
            </>
        )
    }

    const handleUpdateCart = async (variant, quantity, promotion) => {
        await handleAddToCart(variant, quantity, promotion)
    }

    const handleAddToCart = async (variant, quantity, promotion) => {
        try {
            setCartItemLoading(true)
            if ((!variant?.orderable && !variant?.inventory?.orderable) || !quantity) return
            // The basket accepts an array of `ProductItems`, so lets create a single
            // item array to add to the basket.
            const productItems = [
                {
                    productId: variant.productId || variant.id,
                    quantity,
                    price: variant.price
                }
            ]
            // in case of selection of any other product for the promotion we have to delete the previous selected promotion item
            // theoretically it is one of the product line items but once there are more than one promotions in the basket
            // it becomes harder to find it directly through the product line items and that why we stick to the approach of only
            // betting the already associated line item to this promotion.
            // in case it is missing then we are sure that it is not already selected
            if (promotion && promotion.productItem && promotion.productItem[0]) {
                await removeItemFromBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: promotion.productItem[0].itemId
                    }
                })
                trackRemoveFromCart(promotion.productItem[0], basket, productItemsDetail)
            }

            await addItemToBasketMutation.mutateAsync({productItems})
            // einstein
            einstein.sendAddToCart(
                productItems.map((item) => ({
                    ...item,
                    masterId: variant?.master?.masterId
                }))
            )
            // analytics
            trackAddToCart(
                selectedItem,
                {...productItems, productName: variant.name},
                basket,
                productItemsDetail
            )
        } catch (error) {
            showError(error)
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    const handleRemoveItem = async (product) => {
        setSelectedItem(product)
        setCartItemLoading(true)
        await removeItemFromBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket.basketId, itemId: product.itemId}
            },
            {
                onSettled: () => {
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                },
                onSuccess: () => {
                    toast({
                        title: formatMessage({
                            defaultMessage: 'Item removed from cart',
                            id: 'cart.info.removed_from_cart'
                        })
                    })
                    trackRemoveFromCart(product, basket, productItemsDetail)
                },
                onError: () => {
                    showError()
                }
            }
        )
    }

    const syncAndRemoveItem = (product) => {
        // Flush last call to keep ui in sync with data.
        changeItemQuantity.flush()?.then(() => {
            // need to re-select the item after flushing
            // as the flush will unselect the item
            setSelectedItem(product)
        })
        handleRemoveItem(product)
    }

    const handleChangeItemQuantity = async (product, value) => {
        const {ats = 0} = productItemsDetail[product.productId]?.inventory || {}

        // Handle removing of the items when 0 is selected.
        if (value === 0) {
            syncAndRemoveItem(product)

            // Return false as 0 isn't valid section.
            return false
        }

        // Cancel any pending handlers.
        changeItemQuantity.cancel()

        // This local state allows the dropdown to show the desired quantity
        // while the API call to update it is happening.
        setLocalQuantity((localQuantity) => ({...localQuantity, [product.itemId]: value}))

        // Allow use to selected values above the inventory.
        if (value > ats) {
            return false
        }
        if (value === product.quantity) {
            return true
        }

        // Take action.
        changeItemQuantity(value, product)

        return true
    }

    return (
        <>
            <HeaderVariant bg="background.section.subtle" />
            <Seo
                title={formatMessage({
                    id: 'checkout.page.title',
                    defaultMessage: 'My Cart'
                })}
                description="Cart Page"
                noIndex={true}
            />
            <Box
                flex="1"
                data-testid="sf-cart-container"
                backgroundColor="background.section.subtle"
            >
                <Container variant="no-padding">
                    <ContentSlot slotId="cart-banner" showError={false} variant="cms" />
                    <Grid
                        templateColumns={{base: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 25rem'}}
                        templateRows={{base: 'repeat(3, auto)', lg: 'repeat(2, auto)'}}
                    >
                        {/* cart */}
                        <GridItem>
                            <Stack
                                paddingX={{base: 4, lg: 8}}
                                paddingTop={{base: 10, lg: 16}}
                                paddingBottom={12}
                                spacing={8}
                            >
                                <CartTitle />
                                <Box>
                                    <Divider colorScheme="stokkeGray" />
                                    {shippingMessages && shippingMessages.length > 0 && (
                                        <>
                                            {shippingMessages.map(
                                                (shippingMessage, index) =>
                                                    shippingMessage && (
                                                        <VStack
                                                            key={index}
                                                            alignItems={'start'}
                                                            spacing={3}
                                                            py={6}
                                                        >
                                                            <HStack>
                                                                <Box position={'relative'}>
                                                                    <FreeShippingFlagIcon
                                                                        boxSize={4.5}
                                                                    />
                                                                    <Box
                                                                        position={'absolute'}
                                                                        right={-0.5}
                                                                        bottom={-0.5}
                                                                    >
                                                                        <FreeShippingCircleIcon
                                                                            boxSize={3}
                                                                        />
                                                                    </Box>
                                                                </Box>

                                                                <Text textStyle="Semi Bold Small">
                                                                    <FormattedMessage
                                                                        defaultMessage="You’re {0} away from free shipping."
                                                                        id="cart.shipping.promo"
                                                                        values={{
                                                                            0: (
                                                                                <FormattedNumber
                                                                                    style="currency"
                                                                                    currency={
                                                                                        basket.currency
                                                                                    }
                                                                                    value={
                                                                                        shippingMessage.distanceFromThreshold
                                                                                    }
                                                                                />
                                                                            )
                                                                        }}
                                                                    />
                                                                </Text>
                                                            </HStack>
                                                            <HStack width="100%" spacing={0}>
                                                                <Progress
                                                                    width="100%"
                                                                    borderRadius="3px"
                                                                    height="6px"
                                                                    colorScheme="green"
                                                                    size="sm"
                                                                    aria-label={formatMessage(
                                                                        {
                                                                            id: 'cart.shipping.promo',
                                                                            defaultMessage:
                                                                                'You’re {0} away from free shipping.'
                                                                        },
                                                                        {
                                                                            0: formatNumber(
                                                                                shippingMessage.distanceFromThreshold,
                                                                                {
                                                                                    style: 'currency',
                                                                                    currency:
                                                                                        basket.currency
                                                                                }
                                                                            )
                                                                        }
                                                                    )}
                                                                    value={
                                                                        (1 -
                                                                            shippingMessage.distanceFromThreshold /
                                                                                shippingMessage.threshold) *
                                                                        100
                                                                    }
                                                                />
                                                                <Text
                                                                    textStyle="Semi Bold Small"
                                                                    pl={3}
                                                                >
                                                                    <FormattedNumber
                                                                        style="currency"
                                                                        currency={basket.currency}
                                                                        value={
                                                                            shippingMessage.threshold
                                                                        }
                                                                    />
                                                                </Text>
                                                            </HStack>
                                                        </VStack>
                                                    )
                                            )}
                                        </>
                                    )}
                                    {isShippingPromoApplied && (
                                        <VStack key={0} alignItems={'center'} spacing={3} py={6}>
                                            <VerifiedIcon color="green" boxSize={9} />
                                            <Text textStyle="Semi Bold Small">
                                                <FormattedMessage
                                                    defaultMessage="Yayy. You’ve reached Free Shipping."
                                                    id="cart.shipping.promo.success"
                                                />
                                            </Text>
                                            <HStack width="100%" spacing={0}>
                                                <Progress
                                                    width="100%"
                                                    borderRadius="3px"
                                                    height="6px"
                                                    colorScheme="green"
                                                    size="sm"
                                                    aria-label={formatMessage({
                                                        id: 'cart.shipping.promo.success',
                                                        defaultMessage:
                                                            'Yayy. You’ve reached Free Shipping.'
                                                    })}
                                                    value={100}
                                                />
                                                <Text textStyle="Semi Bold Small" pl={3}>
                                                    <FormattedNumber
                                                        style="currency"
                                                        currency={basket?.currency}
                                                        value={
                                                            basket?.orderTotal ||
                                                            basket?.productTotal
                                                        }
                                                    />
                                                </Text>
                                            </HStack>
                                        </VStack>
                                    )}
                                    {(shippingMessages?.length > 0 || isShippingPromoApplied) && (
                                        <Divider colorScheme="stokkeGray" />
                                    )}
                                </Box>
                                <Stack spacing={4}>
                                    {basket.productItems.map(
                                        (productItem) =>
                                            !productItem?.bonusDiscountLineItemId && (
                                                <CartLineItem
                                                    key={productItem.itemId}
                                                    item={productItem}
                                                    product={
                                                        productItemsDetail[productItem.productId]
                                                    }
                                                    quantity={localQuantity[productItem.itemId]}
                                                    isLoading={
                                                        isCartItemLoading &&
                                                        selectedItem?.itemId === productItem.itemId
                                                    }
                                                    onChangeQuantity={handleChangeItemQuantity}
                                                    onRemove={syncAndRemoveItem}
                                                />
                                            )
                                    )}
                                    {basket?.bonusDiscountLineItems &&
                                        basket?.bonusDiscountLineItems.map((item) => {
                                            const bonusProductItem = basket.productItems?.find(
                                                ({bonusDiscountLineItemId}) =>
                                                    bonusDiscountLineItemId === item.id
                                            )
                                            return (
                                                <React.Fragment key={item.id}>
                                                    <CartBonusOffer
                                                        variant="bonus"
                                                        bonusPromotion={item}
                                                        product={bonusProductItem}
                                                        addToCart={handleAddToCart}
                                                        updateCart={handleUpdateCart}
                                                        setSelectedItem={setSelectedItem}
                                                        notifyMe={onOpenNotifyMeModal}
                                                        includeInAddToCartModal={false}
                                                    />
                                                    {bonusProductItem && (
                                                        <CartLineItem
                                                            isBonusItem
                                                            item={bonusProductItem}
                                                            product={
                                                                productItemsDetail[
                                                                    bonusProductItem.productId
                                                                ]
                                                            }
                                                            isLoading={
                                                                isCartItemLoading &&
                                                                selectedItem?.itemId ===
                                                                    bonusProductItem.itemId
                                                            }
                                                            onRemove={syncAndRemoveItem}
                                                        />
                                                    )}
                                                </React.Fragment>
                                            )
                                        })}
                                </Stack>
                                <Divider colorScheme="stokkeGray" />

                                {giftMessageEnabled && (
                                    <>
                                        <GiftMessage
                                            execute={updateGiftMessage}
                                            loading={updateBasketMutation.isLoading}
                                            data={giftMessageData}
                                        />
                                        <Divider colorScheme="stokkeGray" />
                                    </>
                                )}
                                <CartDelivery shippingMethods={shippingMethods} />
                                {!isDesktop && (
                                    <ExpressCheckout
                                        containerProps={{
                                            paddingX: 4,
                                            paddingBottom: 12,
                                            // the sticky cta has the total sum bellow the button when it is sticky
                                            // and it's hidden when not sticky but still occupies space
                                            // so hack it to make the 32px spacing with the button
                                            marginTop: -2.5
                                        }}
                                    />
                                )}
                            </Stack>
                        </GridItem>

                        {/* Product Recommendations */}
                        {!isProductItemsDetailLoading && (
                            <ModuleContainer
                                background="subtle"
                                spacing="recommender"
                                className="is-app-cart-recommendation"
                            >
                                {marketingCookieAccepted ? (
                                    <RecommendedProducts
                                        title={
                                            <FormattedMessage
                                                defaultMessage="You may also like"
                                                id="recommended_products.title.you_may_also_like"
                                            />
                                        }
                                        recommender={`recom_Basket_${site.id}`}
                                        products={cartProducts}
                                        shouldFetch={() =>
                                            basket?.basketId && basket.productItems?.length > 0
                                        }
                                        itemsPerSlide={{base: 1.7, s: 3}}
                                        onRecommendedProductClick={
                                            productQuickviewModalProps.onOpen
                                        }
                                    />
                                ) : (
                                    <RecommendedAccessories
                                        products={cartProducts}
                                        onRecommendedProductClick={
                                            productQuickviewModalProps.onOpen
                                        }
                                        shouldFetch={() =>
                                            basket?.basketId && basket.productItems?.length > 0
                                        }
                                        itemsPerSlide={{base: 1.7, s: 3}}
                                    />
                                )}
                            </ModuleContainer>
                        )}

                        {/* sidebar */}
                        <GridItem
                            colStart={{lg: 2}}
                            rowStart={{lg: 1}}
                            rowEnd={{lg: -1}}
                            backgroundColor="stokkeCore.white"
                        >
                            <Stack
                                paddingX={{base: 4, lg: 8}}
                                paddingTop={{base: 10, lg: 0}}
                                paddingBottom={{base: 4, lg: 8}}
                                spacing={8}
                                position={{lg: 'sticky'}}
                                top={{lg: `${header?.clientHeight}px`}}
                            >
                                <CartOrderSummary />
                                {isDesktop && <CartCta />}
                                <PayLater
                                    amount={basket?.orderTotal || basket?.productTotal}
                                    currency={basket?.currency}
                                    paypalPlacement="cart"
                                    klarnaPlacement="credit-promotion-badge"
                                />
                                {isDesktop && <ExpressCheckout />}

                                <Callout id="pwa-cart-marketing-callout" />
                            </Stack>
                        </GridItem>

                        {!isDesktop && <CartStickyCta />}
                    </Grid>
                    <ContentSlot slotId="cart-banner-bottom" variant="cms" showError={false} />
                </Container>

                {/* TODO: STOK-6056 sticky CTA */}
                {/*<Box*/}
                {/*    h="130px"*/}
                {/*    position="sticky"*/}
                {/*    bottom={0}*/}
                {/*    bg="white"*/}
                {/*    display={{base: 'block', lg: 'none'}}*/}
                {/*    align="center"*/}
                {/*>*/}
                {/*    <CartCta />*/}
                {/*</Box>*/}
            </Box>
            <NotifyModal {...notifyMeModalProps} />

            <ProductQuickviewModal {...productQuickviewModalProps} />
        </>
    )
}

Cart.getTemplateName = () => 'cart'

export default Cart
